import { useEffect, useState, type ReactNode } from "react";

/**
 * Work around a race inside `@astrana/ui`'s `<GraphQLProvider>`:
 *
 *   <GraphQLProvider clientUrl={...} authToken={...}>
 *     <MemberProfile />
 *   </GraphQLProvider>
 *
 * The provider swaps astrana-ui's singleton Apollo client inside a `useEffect`.
 * MemberProfile's module hooks (useEligibility, useMedicalClaims, …) each run
 * `useMemo(() => getApolloClient(), [])` — memoized with empty deps — so they
 * resolve the singleton on **first render**, which happens before the
 * provider's effect. That first-render capture is the default-URL client
 * (`http://localhost:4000/graphql` + no auth), and the later swap never
 * reaches the already-memoized refs.
 *
 * This gate sits between the provider and its children. On the first pass
 * it renders nothing — so the module hooks don't run yet. Its own
 * `useEffect` flips `ready` to true; that state update schedules a re-render.
 * Effects in React commit bottom-up, so by the time the gate's effect fires
 * the provider's effect has either already run or is about to. Either way,
 * the re-render that follows is when the children actually mount, and their
 * `useMemo` captures the swapped singleton.
 *
 * The gate's `key` used to carry the live token's VALUE, so every MSAL-silent
 * renewal remounted the whole subtree to shake off a client holding the
 * expired bearer. As of `@astrana/ui` v0.4.90 (AM-5828) the auth link reads
 * the bearer live per request, so a rotation needs no remount at all — and the
 * remount was destructive, wiping in-progress UI such as a part-filled Create
 * Claim form. Callers now key on SIGN-IN STATE only ("auth"/"anon"); a change
 * of signed-in identity is handled by `<GraphQLProvider identityKey>`, which
 * rebuilds the client so a new user can't read the previous one's cache.
 */
export function GraphQLClientGate({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(true);
  }, []);
  return ready ? <>{children}</> : null;
}
